import axios from "axios"
import {CONSTANTS} from "../meta_data";
import {ResourceInvalidationTriggers} from "../../global/resourcemanager/resourceManager";
import {BoxRelativeToPart, OwayoKonfiPart} from "../owayoPartWithSelObs";
import {PortalLine, PortalLinieAufPart} from "../portallinie";
import {valuesAreEqualWithinPrecision} from "../../global/globalUtils";
import {Snap} from "../snap";
import {EnumDictionary, Strecke, StringDictionary, Vector2, XY} from "../delme_globals";
import globalsObject from "../globalsObject";
import {translate} from "../globalsObject";
import {arrayGetElementByNoneCaseSensitiveName} from "../../global/arrayHelper";
import {warnIfDebug} from "../../global/logFunctions";
import {owayoUtils} from "../owayoUtils";

export type PartsJSONEntry = {
    hoeheCM: number;
    breiteCM: number;
    groesseX: number;
    groesseY: number;
    hoehenfaktor: number;
    PatternAnker: { CenterX, CenterY } | undefined;
    Druckbereich: { LeftX, RightX, BottomY, TopY } | undefined;
    NichtDruckbereich: { LeftX, RightX, BottomY, TopY } | undefined;
    standardVordrehungVonNeuPlatziertenObjektenImKonfi
}

export class PartJSONParser {

    private static findPart(partname, modelParts) {
        for (const modelPartname in modelParts) {
            if (partname.toLowerCase() === modelPartname.toLowerCase()) {
                return modelParts[modelPartname];
            }
        }
        return null;
    }

    static parsePartsJSON(partsJSON, modelparts) {
        for (const objectName in partsJSON) {
            const part = PartJSONParser.findPart(objectName, modelparts);
            if (part !== null) {
                PartJSONParser.infusePartWithInfoFromJSON(part, partsJSON[objectName])
            }
        }
        globalsObject.resourceManager.executeInvalidation(ResourceInvalidationTriggers.PARTSJSON_PARSED);
    }

    private static getBox(propname: string, parsedPartJSON: PartsJSONEntry) {
        if (typeof (parsedPartJSON[propname]) !== "undefined") {
            return new BoxRelativeToPart(
                new Vector2(parsedPartJSON[propname].LeftX, parsedPartJSON[propname].BottomY),
                new Vector2(parsedPartJSON[propname].RightX, parsedPartJSON[propname].TopY)
            );
        }
        return null;
    }

    private static getDruckBereich = (parsedPartJSON: PartsJSONEntry) => PartJSONParser.getBox("Druckbereich", parsedPartJSON);
    private static getNichtDruckBereich = (parsedPartJSON: PartsJSONEntry) => PartJSONParser.getBox("NichtDruckbereich", parsedPartJSON);
    private static getMotivBereich = (parsedPartJSON: PartsJSONEntry) => PartJSONParser.getBox("Motivbereich", parsedPartJSON);

    private static getPatternAnker(parsedPartJSON: PartsJSONEntry) {
        if (typeof (parsedPartJSON.PatternAnker) !== "undefined") {
            return new Vector2(parsedPartJSON.PatternAnker.CenterX, parsedPartJSON.PatternAnker.CenterY);
        }
        return null;
    }

    private static getUsedScaleBecauseOfMaxTextureSize(groesseX, groesseY) {
        // Da wir aus Performancegründen oder aus Device gründen eine andere MaxTexureSize haben
        // Müssen wir uns hier evtl damit auseinander setzen.
        const scaleX = CONSTANTS.PERFORMANCE_MAX_TEXTURESIZE / groesseX;
        const scaleY = CONSTANTS.PERFORMANCE_MAX_TEXTURESIZE / groesseY;
        return Math.min(scaleX, scaleY, 1.0);
    }

    private static infusePartWithInfoFromJSON(part: OwayoKonfiPart, parsedPartJSON: PartsJSONEntry) {
        part.hasZentralesAbgelegtesNichtEinfaerbbaresPartImage = false;
        part.partStauchungsFaktor = parsedPartJSON.hoehenfaktor / (parsedPartJSON.groesseY / parsedPartJSON.groesseX);
        part.partSizeCM = new Vector2(parsedPartJSON.breiteCM, parsedPartJSON.hoeheCM);
        part.usedScaleBecauseOfMaxTextureSize = PartJSONParser.getUsedScaleBecauseOfMaxTextureSize(
            parsedPartJSON.groesseX, parsedPartJSON.groesseY
        );
        part.textureSize = new Vector2(
            parsedPartJSON.groesseX * part.usedScaleBecauseOfMaxTextureSize,
            parsedPartJSON.groesseY * part.usedScaleBecauseOfMaxTextureSize
        ).apply(Math.floor);
        part.druckBereich = PartJSONParser.getDruckBereich(parsedPartJSON);
        part.nichtDruckBereich = PartJSONParser.getNichtDruckBereich(parsedPartJSON)
        part.motivBereich = PartJSONParser.getMotivBereich(parsedPartJSON) || PartJSONParser.getDruckBereich(parsedPartJSON);
        part.patternAnker = PartJSONParser.getPatternAnker(parsedPartJSON);

    }
}

export class SperrbezirkeJSONParser {
    public static loadSperrbezirkJSON(cut, model, modelParts) {
        axios.get(CONSTANTS.MODEL_PATH + [cut, model, "sperrbezirke.json"].join("/"))
            .then(response => SperrbezirkeJSONParser.parseSperrbezirkeJSON(response.data, modelParts))
            .catch(() => globalsObject.errorHandler.handleError("mild", "onLoadedModel",
                translate("konf_error_fileMissing", "Verzeihung, ich konnte eine wichtige Datei nicht vom Server laden.")
                + "(sperrbezirke.json)"))
    }

    private static parseSperrbezirkeJSON(sperrbezirkeJSON, modelParts) {
        for (const partname in sperrbezirkeJSON) {
            SperrbezirkeJSONParser.parseSperrbezirkeJSONOnPart(sperrbezirkeJSON, partname, modelParts);
        }
    }

    private static parseSperrbezirkeJSONOnPart(sperrbezirkeJSON, partname, modelParts) {
        const part = modelParts[owayoUtils.getCleanPartName(partname)];
        if (typeof (part) === "undefined") {
            return;
        }
        for (const region in sperrbezirkeJSON[partname]) {
            if (region === "VonUntenCm") {
                const widthOfSperrbezirk = sperrbezirkeJSON[partname].VonUntenCm;
                const relGrenzeY = widthOfSperrbezirk / part.partSizeCM.y;
                part.sperrBezirke.push({dim: "y", from: 0, to: relGrenzeY});
            } else {
                globalsObject.errorHandler.handleError("internal", "parseSperrbezirkeJSON",
                    "unknown value " + region + "in sperrbezirke.json"
                );
                return;
            }
        }
    }
}

export class TeilungsLinienJSONParser {

    public static parseTeilungsLinienJSON(portalJSON, modelParts): PortalLine[] {
        const out: PortalLine[] = [];
        for (let i = 0, lenI = portalJSON.length - 1; i < lenI; i += 2) {
            const firstPart = modelParts[portalJSON[i].Partname];
            const secondPart = modelParts[portalJSON[i + 1].Partname];

            const firstPartNodes = portalJSON[i].Nodes;
            const secondPartNodes = portalJSON[i + 1].Nodes;

            const multiply = (a: Vector2, b: Vector2) => new Vector2(Math.round(a.x * b.x), Math.round(a.y * b.y));

            const streckeA = new Strecke(multiply(firstPartNodes[0], firstPart.textureSize), multiply(firstPartNodes[1], firstPart.textureSize));
            const streckeB = new Strecke(multiply(secondPartNodes[0], secondPart.textureSize), multiply(secondPartNodes[1], secondPart.textureSize));

            if (valuesAreEqualWithinPrecision(streckeA.start.x, streckeA.end.x, 2)) {
                streckeA.start.x = streckeA.end.x;
            }

            if (valuesAreEqualWithinPrecision(streckeB.start.x, streckeB.end.x, 2)) {
                streckeB.start.x = streckeB.end.x;
            }
            const portallineDataA = new PortalLinieAufPart(portalJSON[i].Partname, streckeA, modelParts[portalJSON[i].Partname].partStauchungsFaktor);
            const portallineDataB = new PortalLinieAufPart(portalJSON[i + 1].Partname, streckeB, modelParts[portalJSON[i + 1].Partname].partStauchungsFaktor);
            out.push(new PortalLine(portallineDataA, portallineDataB));
        }
        return out;
    }
}

export class SmartGuidesJSONParser {

    private static getAbsolutKoordinatesFromNodes = (nodes: XY[], textureSize: XY) => nodes.map(
        node => {
            const x = Math.round(node.x * textureSize.x);
            const y = Math.round(node.y * textureSize.y);
            return {x, y}
        });

    public static parseSmartguidesJSON(smartguidesJSON: {
        Partname: string,
        Name: string,
        Nodes: XY[]
    }[], modelParts: StringDictionary<OwayoKonfiPart>) {

        return smartguidesJSON.map(function (smartguidesJSONEntry) {

            let part = modelParts[smartguidesJSONEntry.Partname];
            if (typeof (part) === "undefined") {
                part = arrayGetElementByNoneCaseSensitiveName(modelParts, smartguidesJSONEntry.Partname);
            }
            if (typeof (part) === "undefined") {
                warnIfDebug(smartguidesJSONEntry.Partname + " wurde in parseSnapLines versucht zu setzen, existiert nicht");
                return null;
            }
            const points = SmartGuidesJSONParser.getAbsolutKoordinatesFromNodes(smartguidesJSONEntry.Nodes, part.textureSize);
            if (typeof (smartguidesJSONEntry.Name) === "undefined") {
                smartguidesJSONEntry.Name = "";
            }
            let type;
            if (points.length === 2 && valuesAreEqualWithinPrecision(points[0].x, points[1].x, 0.2)) {
                type = "vertical";
            } else {
                type = "multiLine";
            }
            return new Snap(points, "part", "middle", part.name, type, smartguidesJSONEntry.Name);
        }).filter(s => s !== null);
    }
}
